/**
 * The OCEAN search page (PRD "Problem Discovery Engine"), laid out as Cosmos's
 * search page (components/DirectSearchPage.tsx) is, so the two read as one
 * product: one white card holding
 *
 *   filters pane | search header + results | search history pane
 *
 *   - Filters: Cosmos's own FilterSection / CheckboxList (Goal, Country,
 *     Industry). The pane collapses to a thin rail; below md it is a
 *     full-screen drawer opened from the "Filters" button.
 *   - Header: the search box with Search inside it, then each applied filter
 *     as a removable bubble. It never scrolls away.
 *   - Results scroll on their own; the result count stays pinned at the top.
 *     A search in flight replaces the list with the "Searching…" state (as
 *     Cosmos does), so stale or unrelated results are never shown under a new
 *     search; paging keeps the list on screen, dimmed.
 *   - History: Invite and Saved at the top, then the search history. It
 *     collapses to a rail; below lg it is a drawer opened from "History".
 *
 * The numbers beside each goal, country and industry are live: what that
 * filter would show under the words and the other filters (engine._held_facets).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { CheckboxList, FilterSection, Spinner } from '../DirectSearchPage';
import { InviteIcon } from '../icons';
import { Icon } from './icons';
import { useOcean } from './context';
import type { Collection, CompanyMini, FacetCounts, FilterState, HistoryEntry, Meta, Page, ProblemItem, SearchMeta, Suggestions } from './api';
import { COLLECTIONS_ENABLED } from './flags';
import { Pager, RelevancePill, SaveIcon, Skeleton, fmt, sourceAge } from './ui';

const NOT_STATED = 'Not stated';
export const anyFilter = (f: FilterState): boolean =>
  !!(f.q || f.countries.length || f.industries.length || f.goals.length || f.tags.length);

const ChevronLeft = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const railBtn = 'w-8 h-10 flex items-center justify-center text-grayscale-1200 hover:text-grayscale-1300 hover:bg-grayscale-100 rounded transition-colors';
const collapseBtn = 'text-grayscale-1200 hover:text-grayscale-1300 w-8 h-8 flex items-center justify-center rounded hover:bg-grayscale-100 transition-colors';

// ---------------------------------------------------------------- filters
type FilterKey = 'countries' | 'industries' | 'goals';
export type FilterChange = (next: FilterState, how: { kind: string; value: string; on: boolean }) => void;

/** The filter sections, shared by the desktop pane and the phone's drawer. */
export const FiltersBody: React.FC<{
  meta: Meta | null; facets: FacetCounts | null; applied: FilterState; onChange: FilterChange;
}> = ({ meta, facets, applied, onChange }) => {
  // Goal open as well as Country: OCEAN's goal tags are its first way in.
  const [open, setOpen] = useState<Record<FilterKey, boolean>>({ goals: true, countries: true, industries: false });
  const flip = (k: FilterKey) => setOpen(s => ({ ...s, [k]: !s[k] }));
  const toggle = (key: FilterKey, v: string) => {
    const on = !applied[key].includes(v);
    onChange({ ...applied, [key]: on ? [...applied[key], v] : applied[key].filter(x => x !== v) }, { kind: key, value: v, on });
  };
  const goals = (meta?.goals || []).map(g => ({ value: g.name, label: g.name, count: facets ? facets.goals?.[g.name] ?? 0 : g.problems }));
  // Live counts once the search answers (the dataset's until then). Order as
  // Cosmos's sortAlpha: ticked first, then A-Z among those with matches
  // ("Not stated" last of them), then the ones with none.
  const countries = useMemo(() => {
    const live = facets?.countries;
    const all = (meta?.countries || []).map(c => ({ value: c.name, label: c.name, count: live ? live[c.name] ?? 0 : c.problems }));
    const rank = (o: { value: string; count: number }) =>
      applied.countries.includes(o.value) ? 0 : o.count > 0 ? (o.value === NOT_STATED ? 2 : 1) : 3;
    return all.sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [meta, facets, applied.countries]);
  const industries = (meta?.industries || [])
    .map(i => ({ value: i.name, label: i.name, count: facets ? facets.industries[i.name] ?? 0 : i.problems }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  if (!meta) return <div className="text-xs text-grayscale-1100">Loading filters…</div>;
  return (
    <div className="space-y-3">
      <FilterSection title="Goal" isOpen={open.goals} onToggle={() => flip('goals')}>
        <CheckboxList preserveOrder wrap options={goals} selected={applied.goals} onToggle={v => toggle('goals', v)} />
      </FilterSection>
      <FilterSection title="Country" isOpen={open.countries} onToggle={() => flip('countries')}>
        <CheckboxList preserveOrder searchable options={countries} selected={applied.countries}
                      onToggle={v => toggle('countries', v)} />
      </FilterSection>
      <FilterSection title="Industry" isOpen={open.industries} onToggle={() => flip('industries')}>
        <CheckboxList preserveOrder wrap options={industries} selected={applied.industries}
                      onToggle={v => toggle('industries', v)} />
      </FilterSection>
    </div>
  );
};

/** Desktop filter pane (md and up), collapsible to a rail. */
export const FiltersPane: React.FC<{
  collapsed: boolean; onCollapse: (c: boolean) => void; onClear: () => void; children: React.ReactNode;
}> = ({ collapsed, onCollapse, onClear, children }) => collapsed ? (
  <aside className="hidden md:flex shrink-0 w-10 border-r border-grayscale-200 bg-grayscale-50 items-start justify-center pt-3">
    <button type="button" onClick={() => onCollapse(false)} className={railBtn} title="Expand filters" aria-label="Expand filters">
      <ChevronRight />
    </button>
  </aside>
) : (
  <aside className="hidden md:block w-[17rem] lg:w-[19rem] border-r border-grayscale-200 overflow-y-auto p-4 bg-grayscale-50 shrink-0"
         aria-label="Filters">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-base font-semibold text-grayscale-1300">Filters</h2>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onClear} className="text-xs text-primary-600 hover:underline">Clear all</button>
        <button type="button" onClick={() => onCollapse(true)} className={collapseBtn} title="Collapse filters" aria-label="Collapse filters">
          <ChevronLeft />
        </button>
      </div>
    </div>
    {children}
  </aside>
);

/** The phone's filters (below md): a full-screen drawer, as on Cosmos. */
export const FiltersDrawer: React.FC<{ onClose: () => void; onClear: () => void; children: React.ReactNode }> =
  ({ onClose, onClear, children }) => (
    <div className="fixed inset-0 z-[60] md:hidden flex bg-white">
      <aside className="relative h-full w-full bg-white overflow-y-auto p-4 flex flex-col" aria-label="Filters">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-grayscale-1300">Filters</h2>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClear} className="text-xs text-primary-600 hover:underline">Clear all</button>
            <button type="button" onClick={onClose} aria-label="Close filters"
                    className="text-grayscale-1100 hover:text-grayscale-1300 text-xl leading-none w-7 h-7 flex items-center justify-center">×</button>
          </div>
        </div>
        <div className="flex-1">{children}</div>
        <button type="button" onClick={onClose}
                className="mt-3 w-full px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium text-center">Done</button>
      </aside>
    </div>
  );

// ---------------------------------------------------------------- search header
interface Bubble { key: string; group: string; label: string; remove: () => void }

export const SearchHeader: React.FC<{
  query: string; setQuery: (q: string) => void; onSubmit: () => void; searching: boolean;
  applied: FilterState; onChange: (next: FilterState) => void; onClear: () => void;
  historyCount: number; onOpenFilters: () => void; onOpenHistory: () => void;
  error: string; onRetry: () => void;
}> = ({ query, setQuery, onSubmit, searching, applied, onChange, onClear, historyCount, onOpenFilters, onOpenHistory,
       error, onRetry }) => {
  const bubbles: Bubble[] = [
    ...(applied.q ? [{ key: 'q', group: 'Keyword', label: applied.q, remove: () => onChange({ ...applied, q: '' }) }] : []),
    ...applied.goals.map(g => ({ key: `g${g}`, group: 'Goal', label: g, remove: () => onChange({ ...applied, goals: applied.goals.filter(x => x !== g) }) })),
    ...applied.tags.map(t => ({ key: `t${t}`, group: 'Tag', label: t, remove: () => onChange({ ...applied, tags: applied.tags.filter(x => x !== t) }) })),
    ...applied.countries.map(c => ({ key: `c${c}`, group: 'Country', label: c, remove: () => onChange({ ...applied, countries: applied.countries.filter(x => x !== c) }) })),
    ...applied.industries.map(i => ({ key: `i${i}`, group: 'Industry', label: i, remove: () => onChange({ ...applied, industries: applied.industries.filter(x => x !== i) }) })),
  ];
  const filtersOn = applied.goals.length + applied.tags.length + applied.countries.length + applied.industries.length > 0;
  return (
    <div className="p-4 border-b border-grayscale-200 relative">
      {/* Filters / History on narrow screens, where the side panes are hidden. */}
      <div className="flex items-center gap-2 mb-3 lg:hidden">
        <button type="button" onClick={onOpenFilters}
                className="md:hidden px-3 py-1.5 text-sm border border-grayscale-300 rounded-lg flex items-center gap-1">
          <span>Filters</span>
          {filtersOn && <span className="bg-primary-500 text-white text-[10px] rounded-full px-1.5 py-0.5">on</span>}
        </button>
        <button type="button" onClick={onOpenHistory} aria-label="Open search history"
                className="px-3 py-1.5 text-sm border border-grayscale-300 rounded-lg flex items-center gap-1">
          <span>History</span>
          {historyCount > 0 && (
            <span className="bg-primary-500 text-white text-[10px] font-medium rounded-full w-5 h-5 inline-flex items-center justify-center">{historyCount}</span>
          )}
        </button>
      </div>
      <form role="search" className="relative" onSubmit={e => { e.preventDefault(); onSubmit(); }}>
        <label htmlFor="oc-q" className="sr-only">Search problem statements by keyword</label>
        <input id="oc-q" type="text" value={query} onChange={e => setQuery(e.target.value)} maxLength={200} enterKeyHint="search"
               placeholder="Search problem statements by keyword"
               className={`w-full px-4 py-3 ${query ? 'pr-40' : 'pr-32'} rounded-xl border border-grayscale-300 focus:border-primary-400 focus:outline-none text-grayscale-1300`} />
        {query && (
          <button type="button" aria-label="Clear the search box" onClick={() => setQuery('')}
                  className="absolute right-[6.75rem] top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-grayscale-1000 hover:text-grayscale-1300 hover:bg-primary-100">
            <Icon name="close" size={14} />
          </button>
        )}
        <button type="submit" disabled={searching}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 text-white rounded-lg disabled:opacity-50 text-sm bg-primary-500 hover:bg-primary-600">
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      {/* Applied filters, right under the box: the panes may be collapsed or off screen. */}
      {bubbles.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {bubbles.map(b => (
            <span key={b.key} title={`${b.group}: ${b.label}`}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-50 pl-2.5 pr-1 py-0.5 text-xs text-grayscale-1300 max-w-full">
              <span className="text-grayscale-1100">{b.group}</span>
              <span className="font-medium truncate max-w-[14rem]">{b.label}</span>
              <button type="button" onClick={b.remove} aria-label={`Remove ${b.group} filter ${b.label}`}
                      className="w-4 h-4 rounded-full hover:bg-primary-200 text-grayscale-1100 leading-none">×</button>
            </span>
          ))}
          {bubbles.length > 1 && (
            <button type="button" onClick={onClear} className="text-xs text-grayscale-1100 underline underline-offset-2 ml-1">Clear all</button>
          )}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600 mt-2" role="alert">
          {error}{' '}
          <button type="button" onClick={onRetry} className="text-primary-700 underline underline-offset-2">Try again</button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- results
/** Words of four or more describe a need: the engine's AI route, ~10-20 s (Backend ocean/engine.py). */
const looksLikeAi = (q: string) => q.split(/\s+/).filter(w => w.length > 2).length >= 4;

const range = (pg: Page<unknown> | null): string => {
  if (!pg || !pg.total) return '';
  const a = (pg.page - 1) * pg.per_page + 1, b = Math.min(pg.total, pg.page * pg.per_page);
  return `Showing ${fmt(a)}–${fmt(b)} of ${fmt(pg.total)}`;
};

/** The full loader for a search in flight (Cosmos's "Searching…"). */
const Searching: React.FC<{ q: string }> = ({ q }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const t = setInterval(() => setSecs(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(t);
  }, [q]);
  const ai = !!q && looksLikeAi(q);
  return (
    <div aria-busy="true">
      <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-grayscale-1100 mb-3">
        <Spinner className="h-4 w-4" />
        <span>
          {!q ? 'Loading problem statements…' : ai ? 'Searching… matching your need, this can take up to 20 seconds' : 'Searching…'}
          {secs >= 5 && <span className="text-grayscale-1000"> · {secs}s</span>}
        </span>
      </div>
      <Skeleton rows={3} />
    </div>
  );
};

const ProblemPreview: React.FC<{ p: ProblemItem; q: string }> = ({ p, q }) => {
  const o = useOcean();
  const saved = o.savedOf('problem', p.id);
  const age = sourceAge(p.source, o.floorLabel);
  const open = () => o.openProblem(p.id, 'results', q ? { q } : undefined);
  return (
    <div className="tcx tcx-card ocp-card" onClick={open}>
      <div className="tcx-head">
        <div className="tcx-head-main">
          <h3 className="tcx-title">
            <button type="button" className="tcx-title-btn" onClick={e => { e.stopPropagation(); open(); }}>{p.statement}</button>
          </h3>
          <div className="tcx-meta">
            <span className="ocp-meta-i"><Icon name="location" size={14} />{p.country || 'Country not stated'}</span>
            <span aria-hidden="true">·</span>
            <span title={age.title}>Source content: {age.text}</span>
          </div>
        </div>
        {typeof p.relevance === 'number' && <div className="tcx-pills"><RelevancePill relevance={p.relevance} /></div>}
        <span className="ocp-save">
          <SaveIcon kind="problem" saved={!!saved} collection={saved?.collection} picker
                    onClick={() => o.chooseCollection('problem', p.id, 'list')} />
        </span>
        {p.tags.length > 0 && (
          <div className="ocp-tags">
            {p.tags.slice(0, 3).map(t => <span key={t} className="tcx-chip">{t}</span>)}
            {p.tags.length > 3 && <span className="tcx-chip" title={p.tags.slice(3).join(', ')}>+{p.tags.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const CompanyPreview: React.FC<{ c: CompanyMini; q: string }> = ({ c, q }) => {
  const o = useOcean();
  const saved = o.savedOf('company', c.id);
  const open = () => o.openCompanyBase(c.id, q ? { q } : undefined, 'results');
  return (
    <div className="tcx tcx-card ocp-card" onClick={open}>
      <div className="tcx-head">
        <div className="tcx-head-main">
          <h3 className="tcx-title">
            <button type="button" className="tcx-title-btn" onClick={e => { e.stopPropagation(); open(); }}>{c.name}</button>
          </h3>
          <div className="tcx-meta">
            <span>{c.industry || 'Industry not stated'}</span>
            <span aria-hidden="true">·</span>
            <span>{c.country || 'Country not stated'}</span>
          </div>
        </div>
        <span className="ocp-save">
          <SaveIcon kind="company" saved={!!saved} collection={saved?.collection} picker
                    onClick={() => o.chooseCollection('company', c.id, 'list')} />
        </span>
      </div>
    </div>
  );
};

export const EmptyState: React.FC<{ s: Suggestions | undefined; onApply: (f: FilterState, type: string) => void }> = ({ s, onApply }) => {
  const scopeNote = (scope?: string) => scope === 'keyword' ? ' (all countries & industries)' : '';
  return (
    <div className="oc-empty">
      <div className="oc-h" style={{ fontSize: 16 }}>No problems here — yet</div>
      <p>An empty result is a signal: this may be white space. Try a nearby goal tag that does have coverage.</p>
      {!!s?.keywords.length && (
        <div className="oc-sugg-group">
          <span className="oc-muted">Fewer words</span>
          <div className="oc-chips" style={{ justifyContent: 'center' }}>
            {s.keywords.map(k => (
              <button key={k.q} type="button" className="oc-sugg" onClick={() => onApply(k.apply, 'keyword')}>
                “{k.q}”<small>{fmt(k.count)}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      {!!s?.goals.length && (
        <div className="oc-sugg-group">
          {s.goals[0].scope === 'keyword' && <span className="oc-muted">Goal tags with coverage{scopeNote('keyword')}</span>}
          <div className="oc-chips" style={{ justifyContent: 'center' }}>
            {s.goals.map(g => (
              <button key={g.goal} type="button" className="oc-sugg" onClick={() => onApply(g.apply, 'goal')}>
                {g.goal}<small>{fmt(g.count)}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      {!!s?.tags.length && (
        <div className="oc-sugg-group">
          <span className="oc-muted">Nearby sub-tags{scopeNote(s.tags[0].scope)}</span>
          <div className="oc-chips" style={{ justifyContent: 'center' }}>
            {s.tags.map(t => (
              <button key={t.tag} type="button" className="oc-sugg" aria-label={`${t.tag} (${t.goal}), ${fmt(t.count)} results`}
                      onClick={() => onApply(t.apply, 'tag')}>
                {t.tag}<small>{fmt(t.count)}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      {!!s?.relax.length && (
        <div className="oc-sugg-group">
          <div className="oc-chips" style={{ justifyContent: 'center' }}>
            {s.relax.map(r => (
              <button key={r.drop} type="button" className="oc-btn oc-btn-sm oc-btn-ghost" onClick={() => onApply(r.apply, 'relax')}>
                {r.label} · {fmt(r.count)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** A section's list kept on screen while its next page loads (Cosmos's inline load). */
const Paging: React.FC<{ busy: boolean; page: number | null; children: React.ReactNode; className?: string }> =
  ({ busy, page, children, className = '' }) => (
    <div aria-busy={busy || undefined} className={busy ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
      {busy && page != null && (
        <div role="status" className="flex items-center gap-2 text-xs text-grayscale-1100 mb-2">
          <Spinner className="h-3.5 w-3.5" />Loading page {fmt(page)}…
        </div>
      )}
      <div className={className}>{children}</div>
    </div>
  );

export const ResultsPane: React.FC<{
  applied: FilterState; searching: boolean; search: SearchMeta | null;
  problems: Page<ProblemItem> | null; companies: Page<CompanyMini> | null; loadingP: boolean; loadingC: boolean;
  suggestions?: Suggestions; compact: boolean;
  problemsRef: React.Ref<HTMLElement>; companiesRef: React.Ref<HTMLElement>;
  onPage: (section: 'problems' | 'companies', n: number) => void;
  onApply: (f: FilterState, type: string) => void; onAnyway: () => void;
}> = ({ applied, searching, search, problems, companies, loadingP, loadingC, suggestions, compact, problemsRef, companiesRef,
       onPage, onApply, onAnyway }) => {
  const [pending, setPending] = useState<{ problems: number | null; companies: number | null }>({ problems: null, companies: null });
  const go = (section: 'problems' | 'companies', n: number) => { setPending(s => ({ ...s, [section]: n })); onPage(section, n); };
  if (searching) return <Searching q={applied.q} />;
  const p = problems?.total ?? 0, c = companies?.total ?? 0;
  // A search with words holds its results (as Cosmos does): "100 matches of 1,846 results".
  const held = applied.q && search && (search.route === 'keyword' || search.route === 'ai') ? search : null;
  const headline = !problems ? ''
    : held?.route === 'keyword' && held.found != null && held.found > p
      ? `${fmt(p)} match${p === 1 ? '' : 'es'} of ${fmt(held.found)} result${held.found === 1 ? '' : 's'}`
    : held ? `${fmt(p)} match${p === 1 ? '' : 'es'}`
    : `${fmt(p)} problem statement${p === 1 ? '' : 's'}`;
  const fromQuery = held?.applied_from_query || [];
  const fixed = held?.corrected || [];
  const nonsense = search?.route === 'nonsense';
  return (
    <>
      {headline && (
        <div className="ocp-count text-xs text-grayscale-1100" aria-live="polite">
          {headline}{' · '}{fmt(c)} compan{c === 1 ? 'y' : 'ies'}
          {fixed.length > 0 && (
            <>{' · '}Showing results for{' '}
              <span className="font-semibold text-grayscale-1300">{fixed.reduce((q, [a, b]) => q.replace(a, b), applied.q)}</span></>
          )}
        </div>
      )}

      {nonsense && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-grayscale-1200 flex flex-wrap items-center gap-x-3 gap-y-2"
             role="status">
          <span>
            <span className="font-medium text-grayscale-1300">That search didn’t look like a topic we could match.</span>{' '}
            Check the spelling, or try a few descriptive words — or search anyway.
          </span>
          <button type="button" onClick={onAnyway}
                  className="rounded-lg bg-white px-2.5 py-1 font-medium text-primary-700 border border-primary-200 hover:bg-primary-50">
            Search anyway
          </button>
        </div>
      )}
      {fromQuery.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <span className="text-xs text-grayscale-1100">✨ Applied from your query:</span>
          {fromQuery.map(x => (
            <span key={x.value} className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-og-600 text-xs font-medium">
              {x.value}
            </span>
          ))}
        </div>
      )}
      <section ref={problemsRef} aria-labelledby="oc-problems-h" className="ocp-sec">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <h2 id="oc-problems-h" className="text-sm font-semibold text-grayscale-1300 m-0">Problem statements</h2>
          <span className="text-xs text-grayscale-1100">{range(problems)}</span>
        </div>
        {!problems ? <Skeleton rows={compact ? 3 : 4} /> : problems.total === 0 ? (
          <EmptyState s={suggestions} onApply={onApply} />
        ) : (
          <Paging busy={loadingP} page={pending.problems} className="space-y-3">
            {problems.items.map(x => <ProblemPreview key={x.id} p={x} q={applied.q} />)}
          </Paging>
        )}
        {problems && <Pager page={problems.page} pages={problems.pages} onPage={n => go('problems', n)} label="Problem statements"
                            busy={loadingP} />}
      </section>

      <section ref={companiesRef} aria-labelledby="oc-companies-h" className="ocp-sec mt-6 pt-5 border-t border-grayscale-200">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <h2 id="oc-companies-h" className="text-sm font-semibold text-grayscale-1300 m-0">
            Companies{companies && companies.total === 0 ? ' · 0' : ''}
          </h2>
          <span className="text-xs text-grayscale-1100">{range(companies)}</span>
        </div>
        {!companies ? <Skeleton rows={2} /> : companies.total === 0 ? (
          <div className="text-sm text-grayscale-1100">No companies match these filters.</div>
        ) : (
          <Paging busy={loadingC} page={pending.companies} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {companies.items.map(x => <CompanyPreview key={x.id} c={x} q={applied.q} />)}
          </Paging>
        )}
        {companies && <Pager page={companies.page} pages={companies.pages} onPage={n => go('companies', n)} label="Companies"
                             busy={loadingC} />}
      </section>
    </>
  );
};

// ---------------------------------------------------------------- history pane
const historySub = (h: HistoryEntry): string => {
  const f = h.filters || {};
  const bits = [...(f.goals || []), ...(f.tags || []), ...(f.countries || []), ...(f.industries || [])];
  const shown = bits.slice(0, 3).join(', ') + (bits.length > 3 ? ` +${bits.length - 3}` : '');
  return [`${fmt(h.hits)} hits`, shown].filter(Boolean).join(' · ');
};

interface HistoryProps {
  history: HistoryEntry[]; collections: Collection[]; savedCount: number; onReplay: (h: HistoryEntry) => void;
  onDelete: (id: number) => void; onClear: () => void; onOpenSaved: (collectionId?: number) => void; onInvite: () => void;
}

/** Invite and Saved, where Cosmos keeps Invite / Feedback / Bug. */
const ActionButtons: React.FC<{ savedCount: number; onInvite: () => void; onOpenSaved: () => void }> = ({ savedCount, onInvite, onOpenSaved }) => (
  <div className="flex flex-row items-center justify-around gap-1 sm:gap-2">
    <button type="button" onClick={onInvite} title="Find us interesting and helpful? Invite your friends, colleagues, or team to join us!"
            className="flex flex-col justify-center items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-grayscale-100 transition-colors">
      <InviteIcon />
      <span className="text-[10px] text-grayscale-1100 leading-none">Invite</span>
    </button>
    <button type="button" onClick={onOpenSaved} title="Your saved OCEAN cards, in your Profile"
            className="flex flex-col justify-center items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-grayscale-100 transition-colors">
      <span className="relative text-[#1B1C1E]">
        <Icon name="bookmark" size={24} />
        {savedCount > 0 && (
          <span className="absolute -top-1 -right-2 bg-primary-500 text-white text-[9px] font-medium rounded-full min-w-[16px] h-4 px-1 inline-flex items-center justify-center">
            {savedCount > 99 ? '99+' : savedCount}
          </span>
        )}
      </span>
      <span className="text-[10px] text-grayscale-1100 leading-none">Saved</span>
    </button>
  </div>
);

const HistoryList: React.FC<HistoryProps & { onPicked?: () => void }> = ({ history, collections, onReplay, onDelete, onOpenSaved, onPicked }) => (
  <>
    {history.length === 0 && <div className="text-xs text-grayscale-1100">Searches you run appear here, so you can come back to them.</div>}
    <ul className="space-y-2">
      {history.map(h => (
        <li key={h.id} className="group relative">
          <button type="button" onClick={() => { onReplay(h); onPicked?.(); }}
                  className="w-full text-left p-2 pr-8 border border-grayscale-200 rounded-lg hover:bg-grayscale-50">
            <div className="text-sm text-grayscale-1300 truncate font-medium pr-5">{h.query || '(filter only)'}</div>
            <div className="text-[11px] text-grayscale-1100 mt-0.5 truncate">{historySub(h)}</div>
          </button>
          <button type="button" onClick={e => { e.stopPropagation(); onDelete(h.id); }}
                  title="Remove from history" aria-label={`Remove “${h.query || 'filters'}” from history`}
                  className="absolute top-1.5 right-1.5 text-grayscale-700 hover:text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-6 h-6 flex items-center justify-center rounded hover:bg-grayscale-100">×</button>
        </li>
      ))}
    </ul>
    {COLLECTIONS_ENABLED && collections.length > 0 && (
      <div className="mt-4 pt-3 border-t border-grayscale-200">
        <h3 className="text-sm font-semibold text-grayscale-1300 mb-2">Collections</h3>
        {collections.map(c => (
          <button key={c.id} type="button" onClick={() => onOpenSaved(c.id)}
                  className="w-full flex items-center justify-between gap-2 py-1 text-sm text-grayscale-1200 hover:text-primary-700">
            <span className="truncate">{c.name}</span>
            <span className="text-xs text-grayscale-1000">{fmt(c.count)}</span>
          </button>
        ))}
      </div>
    )}
  </>
);

/** Desktop history pane (lg and up), collapsible to a rail. */
export const HistoryPane: React.FC<HistoryProps & { collapsed: boolean; onCollapse: (c: boolean) => void }> =
  ({ collapsed, onCollapse, ...props }) => collapsed ? (
    <aside className="hidden lg:flex shrink-0 w-10 border-l border-grayscale-200 bg-white items-start justify-center pt-3">
      <button type="button" onClick={() => onCollapse(false)} className={railBtn} title="Expand search history" aria-label="Expand search history">
        <ChevronLeft />
      </button>
    </aside>
  ) : (
    <aside className="w-64 xl:w-72 shrink-0 border-l border-grayscale-200 overflow-y-auto px-3 py-4 hidden lg:block bg-white"
           aria-label="Search history, saved cards and invites">
      <div className="mb-4 pb-3 border-b border-grayscale-200">
        <ActionButtons savedCount={props.savedCount} onInvite={props.onInvite} onOpenSaved={() => props.onOpenSaved()} />
      </div>
      <div className="flex items-center justify-between gap-2 mb-3 flex-nowrap">
        <h2 className="text-sm font-semibold text-grayscale-1300 whitespace-nowrap">Search history</h2>
        <div className="flex items-center gap-2 shrink-0">
          {props.history.length > 0 && (
            <button type="button" onClick={props.onClear} title="Clear search history"
                    className="text-[11px] whitespace-nowrap text-grayscale-1100 hover:text-red-600 hover:underline">Clear all</button>
          )}
          <button type="button" onClick={() => onCollapse(true)} className={collapseBtn} title="Collapse search history"
                  aria-label="Collapse search history">
            <ChevronRight />
          </button>
        </div>
      </div>
      <HistoryList {...props} />
    </aside>
  );

/** History on narrow screens (below lg): a full-screen drawer, as on Cosmos. */
export const HistoryDrawer: React.FC<HistoryProps & { onClose: () => void }> = ({ onClose, ...props }) => (
  <div className="fixed inset-0 z-[60] lg:hidden flex bg-white">
    <aside className="relative h-full w-full bg-white overflow-y-auto p-4 flex flex-col" aria-label="Search history">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-grayscale-1300">Search history</h2>
        <div className="flex items-center gap-3">
          {props.history.length > 0 && (
            <button type="button" onClick={props.onClear} title="Clear search history"
                    className="text-[11px] text-grayscale-1100 hover:text-red-600 hover:underline">Clear all</button>
          )}
          <button type="button" onClick={onClose} aria-label="Close history"
                  className="text-grayscale-1100 hover:text-grayscale-1300 text-xl leading-none w-7 h-7 flex items-center justify-center">×</button>
        </div>
      </div>
      <div className="mb-4 pb-3 border-b border-grayscale-200">
        <ActionButtons savedCount={props.savedCount} onInvite={() => { onClose(); props.onInvite(); }}
                       onOpenSaved={() => { onClose(); props.onOpenSaved(); }} />
      </div>
      <div className="flex-1"><HistoryList {...props} onPicked={onClose} /></div>
    </aside>
  </div>
);
